import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useAuth from '../hooks/useAuth';
import api from '../utils/api';

const Profile = () => {
    const { user, loadUser } = useAuth();
    const [tab, setTab] = useState('info');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [photoPreview, setPhotoPreview] = useState(null);

    const { register: regInfo, handleSubmit: submitInfo, formState: { errors: errInfo } } = useForm({
        defaultValues: {
            nombre: user?.nombre,
            apellido: user?.apellido,
            cedula: user?.cedula,
            telefono: user?.telefono,
            zona: user?.zona
        }
    });

    const { register: regPass, handleSubmit: submitPass, watch, reset: resetPass, formState: { errors: errPass } } = useForm();
    const { register: regEmail, handleSubmit: submitEmail, reset: resetEmail, formState: { errors: errEmail } } = useForm();

    const showSuccess = (msg) => {
        setSuccess(msg);
        setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const showError = (msg) => {
        setError(msg);
        setSuccess('');
    };

    // Actualizar info básica
    const onUpdateInfo = async (data) => {
        setLoading(true);
        try {
            await api.put('/users/profile', data);
            await loadUser();
            showSuccess('Perfil actualizado correctamente');
        } catch (err) {
            showError(err.response?.data?.error || 'Error actualizando perfil');
        } finally {
            setLoading(false);
        }
    };

    // Cambiar contraseña
    const onChangePassword = async (data) => {
        setLoading(true);
        try {
            await api.put('/users/profile/change-password', {
                password_actual: data.password_actual,
                password_nuevo: data.password_nuevo
            });
            resetPass();
            showSuccess('Contraseña actualizada correctamente');
        } catch (err) {
            showError(err.response?.data?.error || 'Error actualizando contraseña');
        } finally {
            setLoading(false);
        }
    };

    // Cambiar email
    const onChangeEmail = async (data) => {
        setLoading(true);
        try {
            await api.post('/users/profile/change-email', data);
            resetEmail();
            showSuccess('Se envió un correo de confirmación al nuevo email');
        } catch (err) {
            showError(err.response?.data?.error || 'Error procesando solicitud');
        } finally {
            setLoading(false);
        }
    };

    // Subir foto
    const onPhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPhotoPreview(URL.createObjectURL(file));
        const formData = new FormData();
        formData.append('foto', file);
        setLoading(true);
        try {
            await api.post('/users/profile/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            await loadUser();
            showSuccess('Foto actualizada correctamente');
        } catch (err) {
            showError(err.response?.data?.error || 'Error subiendo foto');
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'info', label: 'Información' },
        { id: 'password', label: 'Contraseña' },
        { id: 'email', label: 'Correo' },
        { id: 'foto', label: 'Foto' },
    ];

    return (
        <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Mi Perfil</h2>

            {/* Info del usuario */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 flex items-center gap-5">
                <div className="relative">
                    {photoPreview || user?.foto_url ? (
                        <img
                            src={photoPreview || user.foto_url}
                            alt="Foto"
                            className="w-20 h-20 rounded-full object-cover"
                        />
                    ) : (
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl font-bold"
                            style={{ backgroundColor: 'var(--color-primary)' }}
                        >
                            {user?.nombre?.charAt(0)}
                        </div>
                    )}
                </div>
                <div>
                    <p className="text-xl font-bold text-gray-800">{user?.nombre} {user?.apellido}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <span
                        className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: 'var(--color-secondary)' }}
                    >
                        {user?.role}
                    </span>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-100">
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            onClick={() => { setTab(t.id); setSuccess(''); setError(''); }}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${
                                tab === t.id
                                    ? 'border-b-2 text-white'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            style={tab === t.id ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)', backgroundColor: 'transparent' } : {}}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {/* Mensajes */}
                    {success && (
                        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">
                            {success}
                        </div>
                    )}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">
                            {error}
                        </div>
                    )}

                    {/* Tab: Información */}
                    {tab === 'info' && (
                        <form onSubmit={submitInfo(onUpdateInfo)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                                    <input
                                        {...regInfo('nombre', { required: 'Requerido' })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                    {errInfo.nombre && <p className="text-red-500 text-xs mt-1">{errInfo.nombre.message}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
                                    <input
                                        {...regInfo('apellido', { required: 'Requerido' })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                    {errInfo.apellido && <p className="text-red-500 text-xs mt-1">{errInfo.apellido.message}</p>}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                                <input
                                    {...regInfo('cedula')}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                                    <input
                                        {...regInfo('telefono')}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona</label>
                                    <input
                                        {...regInfo('zona')}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {loading ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                        </form>
                    )}

                    {/* Tab: Contraseña */}
                    {tab === 'password' && (
                        <form onSubmit={submitPass(onChangePassword)} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña actual</label>
                                <input
                                    type="password"
                                    {...regPass('password_actual', { required: 'Requerido' })}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="••••••••"
                                />
                                {errPass.password_actual && <p className="text-red-500 text-xs mt-1">{errPass.password_actual.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                                <input
                                    type="password"
                                    {...regPass('password_nuevo', {
                                        required: 'Requerido',
                                        minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                                        pattern: {
                                            value: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/,
                                            message: 'Debe tener mayúscula, número y carácter especial'
                                        }
                                    })}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="••••••••"
                                />
                                {errPass.password_nuevo && <p className="text-red-500 text-xs mt-1">{errPass.password_nuevo.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nueva contraseña</label>
                                <input
                                    type="password"
                                    {...regPass('confirm', {
                                        required: 'Requerido',
                                        validate: val => val === watch('password_nuevo') || 'Las contraseñas no coinciden'
                                    })}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="••••••••"
                                />
                                {errPass.confirm && <p className="text-red-500 text-xs mt-1">{errPass.confirm.message}</p>}
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {loading ? 'Actualizando...' : 'Actualizar contraseña'}
                            </button>
                        </form>
                    )}

                    {/* Tab: Correo */}
                    {tab === 'email' && (
                        <form onSubmit={submitEmail(onChangeEmail)} className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-lg px-4 py-3">
                                Correo actual: <strong>{user?.email}</strong>
                                {user?.email_pendiente && (
                                    <p className="mt-1 text-xs">Pendiente de confirmar: {user.email_pendiente}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo correo</label>
                                <input
                                    type="email"
                                    {...regEmail('email_nuevo', { required: 'Requerido' })}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="nuevo@correo.com"
                                />
                                {errEmail.email_nuevo && <p className="text-red-500 text-xs mt-1">{errEmail.email_nuevo.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña para confirmar</label>
                                <input
                                    type="password"
                                    {...regEmail('password', { required: 'Requerido' })}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="••••••••"
                                />
                                {errEmail.password && <p className="text-red-500 text-xs mt-1">{errEmail.password.message}</p>}
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {loading ? 'Enviando...' : 'Solicitar cambio de correo'}
                            </button>
                        </form>
                    )}

                    {/* Tab: Foto */}
                    {tab === 'foto' && (
                        <div className="space-y-4">
                            <div className="flex flex-col items-center gap-4">
                                {photoPreview || user?.foto_url ? (
                                    <img
                                        src={photoPreview || user.foto_url}
                                        alt="Foto"
                                        className="w-32 h-32 rounded-full object-cover border-4"
                                        style={{ borderColor: 'var(--color-primary)' }}
                                    />
                                ) : (
                                    <div
                                        className="w-32 h-32 rounded-full flex items-center justify-center text-white text-5xl font-bold"
                                        style={{ backgroundColor: 'var(--color-primary)' }}
                                    >
                                        {user?.nombre?.charAt(0)}
                                    </div>
                                )}
                                <label
                                    className="cursor-pointer text-white font-semibold py-2.5 px-6 rounded-lg"
                                    style={{ backgroundColor: 'var(--color-primary)' }}
                                >
                                    {loading ? 'Subiendo...' : 'Seleccionar foto'}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={onPhotoChange}
                                        className="hidden"
                                        disabled={loading}
                                    />
                                </label>
                                <p className="text-xs text-gray-400">JPG, PNG o WEBP. Máximo 2MB.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;
